/**
 * NotificationNavigationListener
 *
 * Spec 265 Phase 1 — listens for the `{ type: 'NAVIGATE', url }` postMessage
 * sent by the service worker's `notificationclick` handler
 * (web/public/service-worker.js) and client-routes to it via React Router,
 * so tapping a push notification while the app is already open navigates
 * without a full page reload.
 *
 * Before this component existed, the service worker already posted the
 * NAVIGATE message on the task "View" action, but nothing ever listened for
 * it — the message was dead. Mounted inside <BrowserRouter> (see App.tsx)
 * so useNavigate is available; renders nothing.
 *
 * Security (spec 265 § Security): only same-origin relative paths are
 * accepted. A path must start with a single '/' — `//host/evil`,
 * `http://…`, `javascript:…`, and backslash variants are all rejected,
 * since a crafted NAVIGATE message could otherwise redirect the app to an
 * attacker-controlled or protocol-handler URL.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Returns true only for a same-origin relative path: starts with exactly
 * one '/' (not '//', which the browser treats as protocol-relative), and
 * contains no backslash (some browsers normalize '\' to '/', which can be
 * used to smuggle a protocol-relative URL past a naive single-slash check)
 * and no ASCII control characters. The control-character rule matters
 * because the WHATWG URL parser strips tab/newline/CR before parsing, so
 * "/\t/evil.com" would otherwise pass the '//' check yet parse as the
 * protocol-relative "//evil.com" (verified empirically in review).
 */
export function isSafeRelativeUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0) return false;
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false;
  if (url.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(url)) return false;
  return true;
}

export function NotificationNavigationListener(): null {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      const message = event.data;
      if (!message || message.type !== 'NAVIGATE') return;
      if (!isSafeRelativeUrl(message.url)) return;
      navigate(message.url);
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  return null;
}
